import { memo } from "react";

import cc from "classcat";
import { Button, Row } from "@narmi/design_system";
import { Localized, useLocalization } from "@fluent/react";
import Filters from "byzantine/src/filters";
import { format, parseISO } from "date-fns";

import useIsMxIAVEnabled from "../hooks/useIsMxIAVEnabled";

import styles from "./ExternalAccountRow.module.scss";

import type Account from "byzantine/src/Account";
import type { AddAccountMode } from "../../../../../components/settings/linked_accounts/types";

type ExternalAccountRowProps = {
  account: Account;
  onVerify: (account: Account) => void;
  onTriggerInstantVerification: (mode: AddAccountMode) => void;
  // Set when rendered from the dashboard redesign's card/row views, so the
  // balance can drop the legacy fixed-width/right-aligned layout and, for
  // the card view, match the internal account card's balance styling.
  balanceVariant?: "card" | "row";
};

export function ExternalAccountAction({
  account,
  onVerify,
  onTriggerInstantVerification,
  balanceVariant,
}: ExternalAccountRowProps) {
  const isMxIAVEnabled = useIsMxIAVEnabled();
  const { l10n } = useLocalization();
  const date = account.updated_from_source_at
    ? parseISO(account.updated_from_source_at)
    : null;
  const relativeTime = date ? format(date, "MMM do") : "";

  if (account.verified && isMxIAVEnabled && account.synced_with === null) {
    return (
      <div className={styles.instantAdd}>
        <Button
          kind="secondary"
          size="xs"
          label={l10n.getString("external-account-action-iav")}
          onClick={() =>
            onTriggerInstantVerification("upgradeInstantVerification")
          }
        />
      </div>
    );
  }

  if (
    account.verified &&
    isMxIAVEnabled &&
    account.synced_with === "mx" &&
    account.sync_status === "disconnected"
  ) {
    return (
      <div className={styles.instantAdd}>
        <Button
          kind="secondary"
          size="xs"
          label={l10n.getString("external-account-action-iav-revalidate")}
          onClick={() =>
            onTriggerInstantVerification("revalidateInstantVerification")
          }
        />
      </div>
    );
  }

  if (account.verified) {
    // If no relativeTime is available, we won't attempt to show the balance because the balance will also be unset,
    // given they are set concurrently when syncing with the IAV provider.
    if (!relativeTime) return null;
    return (
      <div
        className={cc([
          styles.balance,
          balanceVariant && styles.redesign,
          balanceVariant === "card" && styles.redesignCard,
        ])}
      >
        {account.balances.available}
        <span className={styles.accountInfoDetails}>
          <Localized
            id="external-accounts-balance-as-of"
            vars={{ date: relativeTime }}
          />
        </span>
      </div>
    );
  }

  return (
    <Button
      kind="secondary"
      size="xs"
      label={l10n.getString("external-account-action-verify")}
      onClick={() => onVerify(account)}
    />
  );
}

const ExternalAccountRow = ({
  account,
  onVerify,
  onTriggerInstantVerification,
}: ExternalAccountRowProps) => {
  const accountNumberLastFour = account.number.slice(-4);
  const accountLabel = `${account.nickname || account.name}`;
  const accessibleAccountLabel = `${accountLabel} - ${accountNumberLastFour}`;

  return (
    <li aria-label={accessibleAccountLabel}>
      <Row alignItems="center" justifyContent="space-between">
        <Row alignItems="center">
          <div className={styles.accountInfo}>
            <span className={styles.accountInfoDetailsHeading}>
              {accountLabel}
            </span>
            <Row className={styles.accountInfoDetails} gapSize="xxs">
              <span>{accountNumberLastFour}</span>
              <span className={styles.accountInfoDetailsDot}>•</span>
              <span>{account.fi_name}</span>
              <span className={styles.accountInfoDetailsDot}>•</span>
              <span>{Filters.titlecase(account.product.type)}</span>
            </Row>
          </div>
        </Row>

        <ExternalAccountAction
          account={account}
          onVerify={onVerify}
          onTriggerInstantVerification={onTriggerInstantVerification}
        />
      </Row>
    </li>
  );
};

export default memo(ExternalAccountRow);
